import { GoogleConsentApiType, GoogleConsents, GoogleConsentValues } from "./google-consent-type";
import { LogService } from "../log";
import { FormattedConsentData } from "../consent/consent-type";

interface ConsentListenerCallback {
	(consents: FormattedConsentData): void;
}

class GoogleConsentServiceController {
	// ApiType: GoogleConsentApiTypes;
	private readonly consentCallbacks: Map<string, ConsentListenerCallback>;

	constructor() {
		this.consentCallbacks = new Map();
	}

	setDefaultConsents(consentValues: FormattedConsentData, apiType: GoogleConsentApiType) {
		switch (apiType) {
			case "both":
				LogService.log.debug("Setting default consents with both gtag and GTM API");
				this.setDefaultConsentsGtag(consentValues);
				this.setDefaultConsentsGtm();
				break;
			case "gtag":
				LogService.log.debug("Setting default consents with gtag API");
				this.setDefaultConsentsGtag(consentValues);
				break;
			case "gtmApi":
				LogService.log.debug("Setting default consents with GTM API");
				this.setDefaultConsentsGtm();
				break;
			case "none":
				LogService.log.debug("none selected for Google Consent Service API type. No action taken.");
				break;
			default:
				LogService.log.warn("Invalid API type for Google Consent Service");
				return;
		}
	}

	setDefaultConsentsGtag(consentValues) {
		LogService.log.debug("GOOGLE: Setting default gtag values");

		const googleConsents = this.mapGoogleConsentCategoriesFromDockConsents(consentValues);

		// // ToDo: These should be configurable (opt-in vs. opt-out)
		// const defaultGoogleConsents: GoogleConsents = {
		// 	ad_storage: GoogleConsentValues.Denied,
		// 	ad_user_data: GoogleConsentValues.Denied,
		// 	ad_personalization: GoogleConsentValues.Denied,
		// 	analytics_storage: GoogleConsentValues.Denied,
		// 	personalization_storage: GoogleConsentValues.Denied,
		// };

		window.dataLayer = window.dataLayer || [];

		this.gtag("consent", "default", googleConsents);
		// Make a copy of the dataLayer object to log temporary state
		LogService.log.debug("GOOGLE: Data layer after setting default values: ", { ...window.dataLayer });
	}

	setDefaultConsentsGtm() {
		LogService.log.debug("Setting default consents is done by GTM template/tag...");
		// ...
	}

	updateConsents(consentValues: FormattedConsentData, apiType: GoogleConsentApiType) {
		switch (apiType) {
			case "both":
				LogService.log.debug("Updating consents with both gtag and GTM API");
				this.updateConsentsGtag(consentValues);
				this.updateConsentsGtm(consentValues);
				break;
			case "gtag":
				LogService.log.debug("Updating consents with gtag API");
				this.updateConsentsGtag(consentValues);
				break;
			case "gtmApi":
				LogService.log.debug("Updating consents with GTM API");
				this.updateConsentsGtm(consentValues);
				break;
			case "none":
				LogService.log.debug("none selected for Google Consent Service API type. No action taken.");
				break;
			default:
				LogService.log.warn("Invalid API type for Google Consent Service");
				return;
		}
	}

	async updateConsentsGtag(consentValues: FormattedConsentData) {
		const googleConsents = this.mapGoogleConsentCategoriesFromDockConsents(consentValues);
		LogService.log.debug("Updating gtag with values: ", googleConsents);

		this.gtag("consent", "update", googleConsents);

		// Making a copy of the dataLayer object to log temporary state
		LogService.log.debug("GOOGLE: Data layer after updating values: ", { ...window.dataLayer });
	}
	async updateConsentsGtm(consentValues: FormattedConsentData) {
		//TODO: Consider also using googleConsents directly here
		LogService.log.debug("updateConsentsGtm called with", consentValues);

		for (const [callbackID, callback] of this.consentCallbacks.entries()) {
			LogService.log.debug("Calling consent callback", callbackID);
			callback(consentValues);
		}
	}

	/**
	 *   Called from GTM template to register callback to be executed when user consent is set/updated
	 *   @param {string} callbackId - ID of the callback
	 *   @param {function} callback - Callback to execute on user consent
	 */
	// Arrow function to preserve "this" context!!
	addConsentUpdateCallback = (callbackId: string, callback: () => void) => {
		const setSizeBeforeAdd = this.consentCallbacks.size;

		LogService.log.trace("Consent listener callback. ID:", callbackId, "Callback (as string):", callback.toString());
		if (this.consentCallbacks.has(callbackId)) {
			LogService.log.debug("Consent listener with ID", callbackId, "is already registered. Updating...");
		} else {
			LogService.log.debug("Adding consent listener with ID", callbackId);
		}

		this.consentCallbacks.set(callbackId, callback);
		const setSizeAfterAdd = this.consentCallbacks.size;

		if (setSizeAfterAdd > setSizeBeforeAdd) {
			LogService.log.debug("Consent callback added successfully");
		} else {
			LogService.log.debug("Consent callback was not added (but might have been updated)");
		}
		return setSizeAfterAdd - setSizeBeforeAdd;
	};

	getConsentUpdateCallbacks = () => {
		return this.consentCallbacks;
	};

	dockLoadedDataLayerEvent = (apiType: GoogleConsentApiType) => {
		if (apiType === "gtmApi" || apiType === "gtag" || apiType === "both") {
			this.gtag("event", "dock_loaded");
		}
	};

	gtag(command?: string, action?: string, options?: GoogleConsents) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(arguments);
		LogService.log.debug("GOOGLE: gtag called with arguments: ", command, action, options);
	}

	/*
	 *  Map the DOCK consents (currently categories are convenience, analysis and marketing)
	 *  to the Google Consent Mode (standard) categories.
	 */
	private mapGoogleConsentCategoriesFromDockConsents = dockConsents => {
		const googleConsentModeStates = {
			personalization_storage: dockConsents.convenience ? GoogleConsentValues.Granted : GoogleConsentValues.Denied,
			analytics_storage: dockConsents.analysis ? GoogleConsentValues.Granted : GoogleConsentValues.Denied,
			ad_storage: dockConsents.marketing ? GoogleConsentValues.Granted : GoogleConsentValues.Denied,
			ad_user_data: dockConsents.marketing ? GoogleConsentValues.Granted : GoogleConsentValues.Denied,
			ad_personalization: dockConsents.marketing ? GoogleConsentValues.Granted : GoogleConsentValues.Denied,
		};

		LogService.log.debug("Google Consent Mode States:", googleConsentModeStates);

		return googleConsentModeStates;
	};
}

export const GoogleConsentService = new GoogleConsentServiceController();
